import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { SearchParams, Patient, SearchResponse } from "../search/types";
import { PatientTimelineSession } from "../interfaces/patientTimeline";

/**
 * MPI Data Contract for Store
 *
 * Store key: selectedPatient (Patient | null)
 *
 * Current gaps on refresh:
 * - selectedPatient becomes null when page refreshes
 * - Timeline page needs MPI data to display patient info
 * - usePatientFromUrl creates mock patient with limited data
 *
 * Solution: upsertMpiPatient() rehydrates store with real MPI data
 */

type Tab = "search" | "audit";

export interface PatientDisplayInfo {
  name: string;
  otherNames: string;
  lastName: string;
  otherLastNames: string;
  fullName: string;
  docType: number | null;
  docNumber: string;
  birthDate: string;
  idGender: number | null;
  identificationCountry: number | null;
}

interface PatientStoreState {
  // ----- Form / Búsqueda -----
  searchParams: SearchParams | null;
  formValues: SearchParams;
  searchResults: SearchResponse | null;
  hasSearched: boolean;
  activeTab: Tab;

  // ----- Paciente seleccionado -----
  selectedPatient: Patient | null;

  // ----- Actions (básicas) -----
  setSearchParams: (params: SearchParams) => void;
  setFormValues: (values: SearchParams) => void;
  setSearchResults: (results: SearchResponse | null) => void;
  setHasSearched: (searched: boolean) => void;
  setActiveTab: (tab: Tab) => void;

  setSelectedPatient: (patient: Patient | null) => void;
  clearSelectedPatient: () => void;

  // ----- MPI Rehydration -----
  upsertMpiPatient: (patient: Patient) => void;

  // ----- Actions (complejas) -----
  performSearch: (params: SearchParams) => void;
  clearSearch: () => void;
  resetAll: () => void;

  // ----- Computed getters -----
  hasValidSearchParams: () => boolean;
  hasSelectedPatient: () => boolean;
  getPatientFullName: () => string;
  getPatientDisplayInfo: () => PatientDisplayInfo | null;
}

export interface TimelineStoreState {
  // UI
  selectedSessionId: string | null;
  setSelectedSessionId: (id: string | null) => void;

  // Sesión seleccionada completa
  selectedSession: PatientTimelineSession | null;
  setSelectedSession: (session: PatientTimelineSession | null) => void;

  // Estado temporal SOLO para el proceso de verificación
  patientToVerify: Patient | null;
  isVerifying: boolean;
  setPatientToVerify: (patient: Patient | null) => void;
  setIsVerifying: (verifying: boolean) => void;
}

const initialFormValues: SearchParams = {
  docNumber: "",
  name: "",
  otherNames: "",
  lastName: "",
  otherLastNames: "",
  birthDate: "",
};

export const usePatientStore = create<PatientStoreState>()((set, get) => ({
  // ----- Estado inicial -----
  searchParams: null,
  formValues: initialFormValues,
  searchResults: null,
  hasSearched: false,
  activeTab: "search",

  selectedPatient: null,

  // ----- Setters -----
  setSearchParams: (params) => set({ searchParams: params }),
  setFormValues: (values) => set({ formValues: values }),
  setSearchResults: (results) => set({ searchResults: results }),
  setHasSearched: (searched) => set({ hasSearched: searched }),
  setActiveTab: (tab) => set({ activeTab: tab }),

  setSelectedPatient: (patient) => set({ selectedPatient: patient }),
  clearSelectedPatient: () => set({ selectedPatient: null }),

  // ----- MPI Rehydration -----
  upsertMpiPatient: (patient) => {
    set({ selectedPatient: patient });
  },

  // ----- Complejas -----
  performSearch: (params) => {
    set({
      searchParams: params,
      formValues: params,
      hasSearched: true,
      selectedPatient: null,
      searchResults: null, // limpiar resultados anteriores
    });
  },

  clearSearch: () => {
    set({
      searchResults: null,
      selectedPatient: null,
      hasSearched: false,
    });
  },

  resetAll: () => {
    set({
      searchParams: null,
      formValues: initialFormValues,
      searchResults: null,
      selectedPatient: null,
      hasSearched: false,
      activeTab: "search",
    });
  },

  // ----- Computed -----
  hasValidSearchParams: () => {
    const { formValues } = get();
    return Object.values(formValues).some((v) =>
      typeof v === "string" ? v.trim() !== "" : v != null
    );
  },

  hasSelectedPatient: () => get().selectedPatient !== null,

  getPatientFullName: () => {
    const p = get().selectedPatient;
    if (!p) return "";
    return [p.name, p.otherNames, p.lastName, p.otherLastNames]
      .filter(Boolean)
      .join(" ");
  },

  getPatientDisplayInfo: () => {
    const p = get().selectedPatient;
    if (!p) return null;
    const patientInfo = {
      name: p.name || "",
      otherNames: p.otherNames || "",
      lastName: p.lastName || "",
      otherLastNames: p.otherLastNames || "",
      fullName: [p.name, p.otherNames, p.lastName, p.otherLastNames]
        .filter(Boolean)
        .join(" "),
      docNumber: p.docNumber || "",
      docType: typeof p.docType === "number" ? p.docType : 0,
      birthDate: p.birthDate || "",
      idGender: typeof p.idGender === "number" ? p.idGender : null,
      identificationCountry:
        p.identificationCountry !== undefined ? p.identificationCountry : null,
    };
    return patientInfo;
  },
}));

/* ---------- Selectores recomendados (evitan re-renders innecesarios) ---------- */
export const useSearchFormValues = () => usePatientStore((s) => s.formValues);

export const useSearchResults = () => usePatientStore((s) => s.searchResults);

export const useSearchState = () =>
  usePatientStore((s) => ({
    hasSearched: s.hasSearched,
    searchParams: s.searchParams,
  }));

export const useSelectedPatient = () =>
  usePatientStore((s) => s.selectedPatient);

export const useSelectedPatientActions = () => {
  const setSelectedPatient = usePatientStore((s) => s.setSelectedPatient);
  const clearSelectedPatient = usePatientStore((s) => s.clearSelectedPatient);
  const upsertMpiPatient = usePatientStore((s) => s.upsertMpiPatient);

  return {
    setSelectedPatient,
    clearSelectedPatient,
    upsertMpiPatient,
  };
};

export const useTimelineStore = create<TimelineStoreState>()(
  persist(
    (set) => ({
      selectedSessionId: null,
      setSelectedSessionId: (id) => set({ selectedSessionId: id }),

      selectedSession: null,
      setSelectedSession: (session) => set({ selectedSession: session }),

      patientToVerify: null,
      isVerifying: false,
      setPatientToVerify: (patient) => set({ patientToVerify: patient }),
      setIsVerifying: (verifying) => set({ isVerifying: verifying }),
    }),
    {
      name: "timeline-store",
      partialize: (state) => ({
        selectedSessionId: state.selectedSessionId,
        selectedSession: state.selectedSession,
      }),
    }
  )
);
